import React, { useState, useEffect, useCallback } from 'react';

const PHOTO_BASE = window.location.origin;
const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${PHOTO_BASE}${url}`;
};

/**
 * Full-screen photo lightbox with GPS overlay and optional gallery navigation.
 * Props (use EITHER photo OR photos):
 *   photo       - single photo object (backward compat)
 *   photos      - array of photo objects (enables prev/next navigation)
 *   initialIndex- starting index when photos[] is provided (default 0)
 *   entryInfo   - optional string for zone/entry context
 *   onClose     - function to close the lightbox
 *
 * Photo shape: { photo_url, photo_type, latitude, longitude, captured_at }
 */
export default function PhotoLightbox({ photo, photos, initialIndex = 0, entryInfo, onClose }) {
  // Normalize to an array
  const list = photos && photos.length ? photos : (photo ? [photo] : []);
  const [idx, setIdx] = useState(initialIndex);

  useEffect(() => { setIdx(initialIndex); }, [initialIndex, photos]);

  const count = list.length;
  const prev = useCallback((e) => {
    if (e) e.stopPropagation();
    setIdx(i => (i - 1 + count) % count);
  }, [count]);
  const next = useCallback((e) => {
    if (e) e.stopPropagation();
    setIdx(i => (i + 1) % count);
  }, [count]);

  // Keyboard shortcuts: ← → and Esc
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === 'ArrowLeft' && count > 1) prev();
      else if (ev.key === 'ArrowRight' && count > 1) next();
      else if (ev.key === 'Escape') onClose && onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next, count, onClose]);

  if (count === 0) return null;
  const current = list[idx] || list[0];
  const url = getPhotoUrl(current.photo_url);
  const hasGps = current.latitude && current.longitude;

  const arrowBtn = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none',
    width: 48, height: 48, borderRadius: '50%',
    cursor: 'pointer', fontSize: 28, fontWeight: 700, lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.9)', zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', cursor: 'default' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: -40, right: 0, background: 'none',
            border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer',
            fontWeight: 700, lineHeight: 1,
          }}
          aria-label="Close"
        >
          &times;
        </button>

        {/* Counter */}
        {count > 1 && (
          <div style={{
            position: 'absolute', top: -34, left: 0,
            color: '#fff', fontSize: 13, fontWeight: 600,
            background: 'rgba(0,0,0,0.5)', padding: '3px 10px', borderRadius: 12,
          }}>
            {idx + 1} / {count}
          </div>
        )}

        {/* Image */}
        <img
          src={url}
          alt={current.photo_type}
          style={{
            maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8,
            objectFit: 'contain', display: 'block',
          }}
        />

        {/* Prev / Next arrows */}
        {count > 1 && (
          <>
            <button
              onClick={prev}
              style={{ ...arrowBtn, left: 8 }}
              aria-label="Previous"
            >‹</button>
            <button
              onClick={next}
              style={{ ...arrowBtn, right: 8 }}
              aria-label="Next"
            >›</button>
          </>
        )}

        {/* GPS + info overlay at bottom of image */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
          padding: '24px 16px 12px', borderRadius: '0 0 8px 8px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, textTransform: 'capitalize' }}>
                {current.photo_type}
                {entryInfo && <span style={{ fontWeight: 400, marginLeft: 8, opacity: 0.8 }}>{entryInfo}</span>}
              </div>
              {hasGps && (
                <div style={{ color: '#90caf9', fontSize: 13, marginTop: 4 }}>
                  📍 {Number(current.latitude).toFixed(6)}, {Number(current.longitude).toFixed(6)}
                </div>
              )}
              {current.captured_at && (
                <div style={{ color: '#bbb', fontSize: 11, marginTop: 2 }}>
                  {new Date(current.captured_at).toLocaleString()}
                </div>
              )}
            </div>
            {hasGps && (
              <a
                href={`https://www.google.com/maps?q=${current.latitude},${current.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                style={{
                  background: '#1976d2', color: '#fff', padding: '6px 14px',
                  borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Open in Maps
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
